

  // components/ContactUs.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Phone,
  Mail,
  Clock,
  Building2,
  MapPin,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { usePageData } from "../../../hooks/useProfilePageData";

const ContactUs = () => {
  const { pageData, loading, error } = usePageData("contact-us");
  const navigate = useNavigate();

  const parseContactInfo = (description) => {
    if (!description) return {};
    
    const lines = description.split("\n");
    let phone = "";
    let email = "";
    let hours = "";
    let address = "";
    let companyName = "";

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.includes("Phone")) {
        phone = line.replace("Phone", "").replace(":", "").trim();
      }
      if (line.includes("Email")) {
        email = line.replace("Email", "").replace(":", "").trim();
      }
      if (line.includes("Working Hours")) {
        hours = line.replace("Working Hours", "").replace(":", "").trim();
      }
      if (line.includes("Company Details")) {
        companyName = lines[i + 1]?.trim() || "";
        address = lines
          .slice(i + 2, i + 5)
          .join(" ")
          .trim();
      }
    }

    return { phone, email, hours, address, companyName };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading contact information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg shadow-lg p-8 max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!pageData) return null;

  const contactInfo = parseContactInfo(pageData.description);

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 bg-white rounded-lg hover:bg-gray-100 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <h1 className="flex-1 text-center text-xl sm:text-5xl font-bold text-gray-800">
            Contact Us
          </h1>

          <div className="w-[90px]" />
        </div>

        <div className="grid gap-6 md:grid-cols-2 mb-8">
          {contactInfo.phone && (
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Phone className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Phone</h3>
              </div>
              <p className="text-gray-600 text-lg">{contactInfo.phone}</p>
              <p className="text-sm text-gray-500 mt-2">
                Mon-Sat, 9:30 AM - 6:00 PM IST
              </p>
            </div>
          )}

          {contactInfo.email && (
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <Mail className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Email</h3>
              </div>
              <p className="text-gray-600 text-lg break-all">{contactInfo.email}</p>
              <p className="text-sm text-gray-500 mt-2">
                We'll respond within 24 hours
              </p>
            </div>
          )}

          {contactInfo.hours && (
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-purple-100 rounded-full">
                  <Clock className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Working Hours</h3>
              </div>
              <p className="text-gray-600">{contactInfo.hours}</p>
              <p className="text-sm text-red-500 mt-2">
                Closed on Sundays & Public Holidays
              </p>
            </div>
          )}

          {(contactInfo.companyName || contactInfo.address) && (
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 md:col-span-2">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-orange-100 rounded-full">
                  <Building2 className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Company Address</h3>
              </div>
              {contactInfo.companyName && (
                <p className="font-semibold text-gray-800 mb-2">
                  {contactInfo.companyName}
                </p>
              )}
              <div className="flex gap-2 text-gray-600">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p>{contactInfo.address}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactUs;